import { platformApiClient } from './client'
import type {
  ReportConfiguration,
  CreateReportConfiguration,
  UpdateReportConfiguration,
  ReportConfigurationList,
  ReportHistoryList,
  SendReportResponse,
} from '../types'

const PRODUCT_SLUG = 'falcon'

/**
 * Helper to create headers with team ID
 */
const teamHeaders = (teamId: string) => ({
  'X-Team-ID': teamId,
})

export const reportsApi = {
  /**
   * List all report configurations for a team
   */
  async listConfigurations(teamId: string): Promise<ReportConfiguration[]> {
    const response = await platformApiClient.get<ReportConfigurationList>(
      `/v1/notifications/reports/configurations`,
      {
        headers: teamHeaders(teamId),
        params: { product_slug: PRODUCT_SLUG },
      }
    )
    return response.data.configurations
  },

  /**
   * Get a single report configuration
   */
  async getConfiguration(teamId: string, configId: string): Promise<ReportConfiguration> {
    const response = await platformApiClient.get<ReportConfiguration>(
      `/v1/notifications/reports/configurations/${configId}`,
      {
        headers: teamHeaders(teamId),
      }
    )
    return response.data
  },

  /**
   * Create a new report configuration
   */
  async createConfiguration(
    teamId: string,
    data: CreateReportConfiguration
  ): Promise<ReportConfiguration> {
    const response = await platformApiClient.post<ReportConfiguration>(
      `/v1/notifications/reports/configurations`,
      data,
      {
        headers: teamHeaders(teamId),
        params: { product_slug: PRODUCT_SLUG },
      }
    )
    return response.data
  },

  /**
   * Update a report configuration
   */
  async updateConfiguration(
    teamId: string,
    configId: string,
    data: UpdateReportConfiguration
  ): Promise<ReportConfiguration> {
    const response = await platformApiClient.patch<ReportConfiguration>(
      `/v1/notifications/reports/configurations/${configId}`,
      data,
      {
        headers: teamHeaders(teamId),
      }
    )
    return response.data
  },

  /**
   * Delete a report configuration
   */
  async deleteConfiguration(teamId: string, configId: string): Promise<void> {
    await platformApiClient.delete(`/v1/notifications/reports/configurations/${configId}`, {
      headers: teamHeaders(teamId),
    })
  },

  /**
   * Get report history for a team
   */
  async getHistory(
    teamId: string,
    options?: {
      configurationId?: string
      skip?: number
      limit?: number
    }
  ): Promise<ReportHistoryList> {
    const response = await platformApiClient.get<ReportHistoryList>(
      `/v1/notifications/reports/history`,
      {
        headers: teamHeaders(teamId),
        params: {
          product_slug: PRODUCT_SLUG,
          configuration_id: options?.configurationId,
          skip: options?.skip,
          limit: options?.limit,
        },
      }
    )
    return response.data
  },

  /**
   * Send a report immediately
   */
  async sendReportNow(teamId: string, configurationId: string): Promise<SendReportResponse> {
    const response = await platformApiClient.post<SendReportResponse>(
      `/v1/notifications/reports/send`,
      { configuration_id: configurationId },
      {
        headers: teamHeaders(teamId),
      }
    )
    return response.data
  },
}
